import { useState } from 'react';
import { 
  Plus, Edit2, Trash2, Folder, FolderOpen, Save, 
  BookOpen, Calendar 
} from 'lucide-react';
import { Button, Input, Modal, Card } from '../common';
import { useReadingCollections } from '../../hooks/useReadingCollections';
import type { ReadingCollection } from '../../types/collections';
import type { TarotReading } from '../../types/tarot';

interface CollectionManagerProps {
  readings: TarotReading[];
  isOpen: boolean;
  onClose: () => void;
  onSelectCollection?: (collection: ReadingCollection) => void;
}

const colorOptions = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  '#ec4899', '#f43f5e', '#64748b', '#6b7280', '#374151'
];

export default function CollectionManager({ 
  readings, 
  isOpen, 
  onClose, 
  onSelectCollection 
}: CollectionManagerProps) {
  const {
    collections,
    createCollection,
    updateCollection,
    deleteCollection,
    getCollectionsForReading: _getCollectionsForReading
  } = useReadingCollections();

  const [editingCollection, setEditingCollection] = useState<ReadingCollection | null>(null);
  const [showCreateCollection, setShowCreateCollection] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const [newCollection, setNewCollection] = useState({
    name: '',
    description: '',
    color: colorOptions[0]
  });

  const handleCreateCollection = () => {
    if (newCollection.name.trim()) {
      const collection = createCollection({
        name: newCollection.name.trim(),
        description: newCollection.description.trim() || undefined,
        color: newCollection.color,
        readingIds: []
      });
      setNewCollection({ name: '', description: '', color: colorOptions[0] });
      setShowCreateCollection(false);
      
      if (onSelectCollection) {
        onSelectCollection(collection);
      }
    }
  };

  const handleUpdateCollection = () => {
    if (editingCollection) {
      updateCollection(editingCollection.id, {
        name: editingCollection.name.trim(),
        description: editingCollection.description?.trim() || undefined,
        color: editingCollection.color
      });
      setEditingCollection(null);
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      deleteCollection(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const getCollectionStats = (collection: ReadingCollection) => {
    const collectionReadings = readings.filter(r => collection.readingIds.includes(r.id));
    return {
      total: collectionReadings.length,
      favorites: collectionReadings.filter(r => r.isFavorite).length,
      completed: collectionReadings.filter(r => r.status === 'completed').length,
      lastUpdated: collection.updatedAt
    };
  };

  const ColorPicker = ({ 
    value, 
    onChange 
  }: { 
    value: string; 
    onChange: (color: string) => void; 
  }) => (
    <div className="grid grid-cols-10 gap-1">
      {colorOptions.map(color => (
        <button
          key={color}
          onClick={() => onChange(color)}
          className={`w-6 h-6 rounded-full border-2 transition-all ${
            value === color ? 'border-white scale-110' : 'border-transparent'
          }`}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Collection Manager"
      size="lg"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-white">Reading Collections</h3>
            <p className="text-sm text-gray-400">
              Organize your readings into themed collections
            </p>
          </div>
          <Button
            onClick={() => setShowCreateCollection(true)}
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Collection
          </Button>
        </div>

        {/* Collections List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {collections.length === 0 ? (
            <Card className="text-center py-8">
              <Folder className="h-12 w-12 text-gray-500 mx-auto mb-3" />
              <h4 className="text-lg font-medium text-white mb-2">No Collections Yet</h4>
              <p className="text-gray-400 mb-4">
                Create your first collection to organize your readings
              </p>
              <Button onClick={() => setShowCreateCollection(true)}>
                Create Collection
              </Button>
            </Card>
          ) : (
            collections.map(collection => {
              const stats = getCollectionStats(collection);
              return (
                <Card 
                  key={collection.id} 
                  className="p-4 hover:bg-dark-750 transition-colors cursor-pointer"
                  onClick={() => onSelectCollection?.(collection)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="flex-shrink-0 mt-1">
                        {stats.total > 0 ? (
                          <FolderOpen 
                            className="h-5 w-5" 
                            style={{ color: collection.color }}
                          />
                        ) : (
                          <Folder 
                            className="h-5 w-5" 
                            style={{ color: collection.color }}
                          />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white mb-1">
                          {collection.name}
                        </h4>
                        
                        {collection.description && (
                          <p className="text-sm text-gray-400 mb-2">
                            {collection.description}
                          </p>
                        )}
                        
                        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <BookOpen className="h-3 w-3" />
                            {stats.total} readings
                          </span>
                          {stats.favorites > 0 && (
                            <span>{stats.favorites} favorites</span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(stats.lastUpdated).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-1 ml-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCollection(collection);
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(collection.id);
                        }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* Create Collection Modal */}
        {showCreateCollection && (
          <Modal
            isOpen={true}
            onClose={() => setShowCreateCollection(false)}
            title="Create New Collection"
          >
            <div className="space-y-4">
              <Input
                label="Collection Name"
                value={newCollection.name}
                onChange={(e) => setNewCollection(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter collection name..."
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Color
                </label>
                <ColorPicker
                  value={newCollection.color}
                  onChange={(color) => setNewCollection(prev => ({ ...prev, color }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={newCollection.description}
                  onChange={(e) => setNewCollection(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this collection..."
                  rows={3}
                  className="w-full px-4 py-2 rounded border bg-dark-700 text-white placeholder-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-900 border-dark-600 focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowCreateCollection(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateCollection}
                  disabled={!newCollection.name.trim()}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Create Collection
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Edit Collection Modal */}
        {editingCollection && (
          <Modal
            isOpen={true}
            onClose={() => setEditingCollection(null)}
            title="Edit Collection"
          >
            <div className="space-y-4">
              <Input
                label="Collection Name"
                value={editingCollection.name}
                onChange={(e) => setEditingCollection(prev => 
                  prev ? { ...prev, name: e.target.value } : null
                )}
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Color
                </label>
                <ColorPicker
                  value={editingCollection.color}
                  onChange={(color) => setEditingCollection(prev => 
                    prev ? { ...prev, color } : null
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={editingCollection.description || ''}
                  onChange={(e) => setEditingCollection(prev => 
                    prev ? { ...prev, description: e.target.value } : null
                  )}
                  rows={3}
                  className="w-full px-4 py-2 rounded border bg-dark-700 text-white placeholder-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-900 border-dark-600 focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setEditingCollection(null)}
                >
                  Cancel
                </Button>
                <Button onClick={handleUpdateCollection}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <Modal
            isOpen={true}
            onClose={() => setDeleteConfirm(null)}
            title="Delete Collection"
          >
            <div className="space-y-4">
              <p className="text-gray-300">
                Are you sure you want to delete this collection? The readings will not be deleted, 
                but they will be removed from this collection.
              </p>

              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteConfirm}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete Collection
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </Modal>
  );
}